import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CustomDropdown } from "../SpotOrders/spotOrderUi";
import SoMobileFilters from "./SoMobileFilters";
import OrdersSubTabs from "./OrdersSubTabs";
import OrderMobileCardShell from "./OrderMobileCardShell";
import AuthService from "../../../api/services/AuthService";
import {
  decNum,
  decStr,
  fmtTs,
  formatOptionsOpenOrderPrice,
  formatOptionsOrderType,
  isReduceOnlyIntent,
  optionsOpenOrderAvgFill,
} from "../options/optionsDataHelpers";

const ORDER_DESKTOP_COL_COUNT = 11;

const CONTRACT_OPTIONS = [
  { value: "all", label: "All" },
  { value: "btcusdt", label: "BTCUSDT" },
  { value: "ethusdt", label: "ETHUSDT" },
  { value: "bnbusdt", label: "BNBUSDT" },
];

function extractOpenOrders(res) {
  if (!res || res.success === false) return [];
  const d = res.data;
  if (Array.isArray(d?.orders)) return d.orders;
  if (Array.isArray(d?.items)) return d.items;
  if (Array.isArray(d)) return d;
  if (Array.isArray(res.orders)) return res.orders;
  if (Array.isArray(res.items)) return res.items;
  return [];
}

/** Dropdown `btcusdt` vs option symbol `BTC-260925-100000-C`. */
function matchesOptionsContractFilter(symbol, filter) {
  if (!filter || filter === "all") return true;
  const sym = String(symbol || "").toUpperCase();
  const fc = String(filter).replace(/[/_]/g, "").toUpperCase();
  if (!sym || !fc) return false;
  if (sym === fc) return true;
  const base = fc.replace(/USDT$|USDC$|BUSD$/, "");
  return Boolean(base) && (sym.startsWith(`${base}-`) || sym.startsWith(fc));
}

function formatQty(v) {
  const n = decNum(v);
  if (!(n > 0) && n !== 0) return "—";
  return decStr(v) || String(n);
}

function mapOpenOrderRow(item, idx) {
  const sideRaw = String(item?.side || "").toUpperCase();
  const side = sideRaw === "BUY" ? "Buy" : sideRaw === "SELL" ? "Sell" : sideRaw || "—";
  const id = item?.order_id || item?._id || item?.client_order_id || `opt-open-${idx}`;
  return {
    id,
    orderId: item?.client_order_id || item?.order_id || item?._id || "—",
    contract: item?.symbol || item?.contract || item?.instrument || "—",
    orderTime: fmtTs(item?.created_at || item?.updated_at),
    side,
    price: formatOptionsOpenOrderPrice(item),
    size: formatQty(item?.quantity),
    unfilled: formatQty(item?.remaining_quantity),
    fillPrice: optionsOpenOrderAvgFill(item),
    executionType: formatOptionsOrderType(item?.order_type),
    reduceOnly: isReduceOnlyIntent(item?.order_intent) ? "Yes" : "No",
    cancellable: Boolean(item?.order_id || item?._id),
  };
}

const OptionsOpenOrders = () => {
  const [contract, setContract] = useState("all");
  const [openDropdown, setOpenDropdown] = useState(null);
  const [rows, setRows] = useState([]);
  const [cancellingId, setCancellingId] = useState(null);

  const loadOrders = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      setRows([]);
      return;
    }
    try {
      const res = await AuthService.optionsOpenOrders();
      setRows(extractOpenOrders(res).map(mapOpenOrderRow));
    } catch {
      setRows([]);
    }
  }, []);

  useEffect(() => {
    void loadOrders();
  }, [loadOrders]);

  useEffect(() => {
    if (!openDropdown) return undefined;
    const onDocClick = (e) => {
      const t = e.target;
      if (!(t instanceof Element)) return;
      if (!t.closest("[data-so-dropdown]")) setOpenDropdown(null);
    };
    document.addEventListener("click", onDocClick, true);
    return () => document.removeEventListener("click", onDocClick, true);
  }, [openDropdown]);

  useEffect(() => {
    if (!openDropdown) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setOpenDropdown(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [openDropdown]);

  const filteredRows = useMemo(() => {
    return rows.filter((row) => matchesOptionsContractFilter(row.contract, contract));
  }, [rows, contract]);

  const resetFilters = () => setContract("all");

  const cancelOpenRow = async (row) => {
    if (!row?.id || !row.cancellable || cancellingId) return;
    setCancellingId(row.id);
    try {
      const res = await AuthService.cancelOptionOrder(row.id);
      if (res?.success) void loadOrders();
    } finally {
      setCancellingId(null);
    }
  };

  const renderToolbar = (stacked, keyPrefix) => {
    const pref = keyPrefix ? `${keyPrefix}-` : "";
    return (
    <div className={`so-toolbar ${stacked ? "so-toolbar--stack" : ""}`} role="search" aria-label="Filter options open orders">
      <div className="so-toolbar__left">
        <CustomDropdown
          id={`${pref}ooo-dd-contract`}
          label="Contract"
          value={contract}
          options={CONTRACT_OPTIONS}
          onChange={setContract}
          menuKey={`${pref}ooo-contract`}
          openKey={openDropdown}
          onOpenToggle={setOpenDropdown}
        />
      </div>
    </div>
    );
  };

  const renderEmptyBody = () => (
    <tr>
      <td colSpan={11} className="so-table__emptyWrap">
        <div className="so-emptyState" role="status">
          <img src="/images/no-data.svg" alt="" width={120} height={144} className="so-emptyState__img" />
        </div>
      </td>
    </tr>
  );

  return (
    <div className="dashboard_right">
      <div className="dashboard_listing_section2 Overview_mid">
        <div className="listing_left_outer full_width transaction_history_t desktop_view2 so-desktopWrap so-optionsOpenOrdersPage">
          <main className="so-page" aria-labelledby="so-options-open-orders-title">
            <h1 id="so-options-open-orders-title" className="so-page__title">
              Open Orders
            </h1>

            <OrdersSubTabs
              tabs={[
                { label: "Open Orders", to: "/user_profile/orders/options_open_orders" },
                { label: "Order History", to: "/user_profile/orders/options_order_history" },
                { label: "Trade History", to: "/user_profile/orders/options_trade_history" },
                { label: "Position History", to: "/user_profile/orders/options_position_history" },
              ]}
            />

            {renderToolbar(false, "desk")}

            <div className="so-tableWrap so-tableWrap--staking so-optionsOpenOrdersTableShell">
              <table className="so-table so-table--rowLines so-table--air so-table--optionsOpenOrders">
                <colgroup>
                  <col className="so-optionsOpenOrders__cw so-optionsOpenOrders__cw--contract" />
                  <col className="so-optionsOpenOrders__cw so-optionsOpenOrders__cw--orderId" />
                  <col className="so-optionsOpenOrders__cw so-optionsOpenOrders__cw--time" />
                  <col className="so-optionsOpenOrders__cw so-optionsOpenOrders__cw--side" />
                  <col className="so-optionsOpenOrders__cw so-optionsOpenOrders__cw--price" />
                  <col className="so-optionsOpenOrders__cw so-optionsOpenOrders__cw--size" />
                  <col className="so-optionsOpenOrders__cw so-optionsOpenOrders__cw--unfilled" />
                  <col className="so-optionsOpenOrders__cw so-optionsOpenOrders__cw--fillPrice" />
                  <col className="so-optionsOpenOrders__cw so-optionsOpenOrders__cw--exec" />
                  <col className="so-optionsOpenOrders__cw so-optionsOpenOrders__cw--reduce" />
                  <col className="so-optionsOpenOrders__cw so-optionsOpenOrders__cw--action" />
                </colgroup>
                <thead>
                  <tr>
                    <th scope="col">Contract</th>
                    <th scope="col">Order ID</th>
                    <th scope="col">Order Time</th>
                    <th scope="col">Side</th>
                    <th scope="col">Price</th>
                    <th scope="col">Size</th>
                    <th scope="col">Unfilled</th>
                    <th scope="col">Fill Price</th>
                    <th scope="col">Exec. Type</th>
                    <th scope="col" className="so-optionsOpenOrders__thReduce">
                      Reduce-Only
                    </th>
                    <th scope="col" className="so-optionsOpenOrders__thAction">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.length === 0
                    ? renderEmptyBody()
                    : filteredRows.map((r) => (
                        <tr key={r.id}>
                          <td className="so-optionsOpenOrders__contract">
                            <strong className="so-optionsOpenOrders__strong">{r.contract}</strong>
                          </td>
                          <td className="so-optionsOpenOrders__cellClip">{r.orderId}</td>
                          <td className="so-num so-optionsOpenOrders__tdTime">{r.orderTime}</td>
                          <td>
                            <span className={r.side === "Buy" ? "so-side--buy so-optionsOpenOrders__pill" : "so-side--sell so-optionsOpenOrders__pill"}>{r.side}</span>
                          </td>
                          <td className="so-num">
                            <strong className="so-optionsOpenOrders__strong">{r.price}</strong>
                          </td>
                          <td className="so-num">{r.size}</td>
                          <td className="so-num">{r.unfilled}</td>
                          <td className="so-num">
                            <strong className="so-optionsOpenOrders__strong">{r.fillPrice}</strong>
                          </td>
                          <td>{r.executionType}</td>
                          <td className="so-optionsOpenOrders__tdReduce">
                            <span className="so-optionsOpenOrders__reduceText">{r.reduceOnly}</span>
                          </td>
                          <td className="so-optionsOpenOrders__tdAction">
                            <button
                              type="button"
                              className="so-cancelBtn so-optionsOpenOrders__cancelBtn"
                              disabled={!r.cancellable || cancellingId === r.id}
                              onClick={() => cancelOpenRow(r)}
                            >
                              {cancellingId === r.id ? "…" : "Cancel"}
                            </button>
                          </td>
                        </tr>
                      ))}
                </tbody>
              </table>
            </div>
          </main>
        </div>

        <div className="order_history_mobile_view so-mobile">
          <h2 className="so-mobile__title">Options Open Orders</h2>
          <OrdersSubTabs
            tabs={[
              { label: "Open Orders", to: "/user_profile/orders/options_open_orders" },
              { label: "Order History", to: "/user_profile/orders/options_order_history" },
              { label: "Trade History", to: "/user_profile/orders/options_trade_history" },
              { label: "Position History", to: "/user_profile/orders/options_position_history" },
            ]}
          />
          <div className="so-mobile__toolbar">
            <SoMobileFilters
              modalId="mob-options-open-filters"
              toolbar={renderToolbar(true, "mob")}
              onReset={resetFilters}
            />
          </div>
          {filteredRows.length === 0 ? (
            <div className="so-emptyState so-emptyState--mobile" role="status">
              <img src="/images/no-data.svg" alt="" width={120} height={144} className="so-emptyState__img" />
            </div>
          ) : (
            filteredRows.map((r) => (
              <OrderMobileCardShell key={`m-${r.id}`} desktopColumnCount={ORDER_DESKTOP_COL_COUNT} popupTitle="Order details">
                <div className="so-mobileCard__row">
                  <span>Contract</span>
                  <span>{r.contract}</span>
                </div>
                <div className="so-mobileCard__row">
                  <span>Order ID</span>
                  <span>{r.orderId}</span>
                </div>
                <div className="so-mobileCard__row">
                  <span>Order Time</span>
                  <span className="so-num">{r.orderTime}</span>
                </div>
                <div className="so-mobileCard__row">
                  <span>Side</span>
                  <span className={r.side === "Buy" ? "so-side--buy" : "so-side--sell"}>{r.side}</span>
                </div>
                <div className="so-mobileCard__row">
                  <span>Price</span>
                  <span className="so-num">{r.price}</span>
                </div>
                <div className="so-mobileCard__row">
                  <span>Size</span>
                  <span className="so-num">{r.size}</span>
                </div>
                <div className="so-mobileCard__row">
                  <span>Unfilled</span>
                  <span className="so-num">{r.unfilled}</span>
                </div>
                <div className="so-mobileCard__row">
                  <span>Fill Price</span>
                  <span className="so-num">{r.fillPrice}</span>
                </div>
                <div className="so-mobileCard__row">
                  <span>Execution Type</span>
                  <span>{r.executionType}</span>
                </div>
                <div className="so-mobileCard__row">
                  <span>Reduce-Only</span>
                  <span>{r.reduceOnly}</span>
                </div>
                <div className="so-mobileCard__actions so-mobileCard__actions--split">
                  <span className="so-mobileCard__statusLabel">Status</span>
                  <button
                    type="button"
                    className="so-cancelBtn"
                    disabled={!r.cancellable || cancellingId === r.id}
                    onClick={() => cancelOpenRow(r)}
                  >
                    {cancellingId === r.id ? "…" : "Cancel"}
                  </button>
                </div>
              </OrderMobileCardShell>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default OptionsOpenOrders;
